/**
 * =============================================================================
 * Componente: AffiliateStatusBadge
 * Versão: 1.0.0
 * Release: 2026-05-02 — Util compartilhado para status de AFILIADO
 *
 * Descrição:
 *   Padroniza a exibição do status de um afiliado/membership (pending /
 *   active / suspended / rejected / inactive) em qualquer tela do sistema.
 *
 *   Visual oficial: "dot colorido + texto" — mesmo padrão de
 *   `ReferralStatusBadge`. Sem badge cheio: apenas bolinha 8px seguida do
 *   label, mantendo as listagens limpas.
 *
 *   Também exporta `AFFILIATE_STATUS_CONFIG` para reuso da cor em barras
 *   laterais (`StatusBar`) e demais elementos auxiliares.
 * =============================================================================
 */
import { cn } from '@/lib/utils';

export type AffiliateStatus =
  | 'pending'
  | 'active'
  | 'suspended'
  | 'rejected'
  | 'inactive';

export const AFFILIATE_STATUS_CONFIG: Record<
  AffiliateStatus,
  { label: string; dot: string; bar: string }
> = {
  // Âmbar: aguardando aprovação
  pending:   { label: 'Pendente',  dot: 'bg-amber-500',           bar: 'bg-amber-500' },
  // Verde: aprovado e ativo
  active:    { label: 'Ativo',     dot: 'bg-green-500',           bar: 'bg-green-500' },
  // Laranja: suspenso temporariamente
  suspended: { label: 'Suspenso',  dot: 'bg-orange-500',          bar: 'bg-orange-500' },
  // Vermelho: reprovado pela administração
  rejected:  { label: 'Reprovado', dot: 'bg-red-500',             bar: 'bg-red-500' },
  // Cinza: desativado/saiu do programa
  inactive:  { label: 'Inativo',   dot: 'bg-muted-foreground/40', bar: 'bg-muted-foreground/40' },
};

interface AffiliateStatusBadgeProps {
  status: string | null | undefined;
  className?: string;
  fallback?: React.ReactNode;
}

export function AffiliateStatusBadge({
  status,
  className,
  fallback,
}: AffiliateStatusBadgeProps) {
  const cfg = status
    ? AFFILIATE_STATUS_CONFIG[status as AffiliateStatus]
    : null;

  if (!cfg) {
    return (
      <>{fallback ?? <span className="text-xs text-muted-foreground">—</span>}</>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium text-foreground whitespace-nowrap',
        className,
      )}
    >
      <span aria-hidden className={cn('h-2 w-2 rounded-full shrink-0', cfg.dot)} />
      {cfg.label}
    </span>
  );
}
